import { action, observable, makeObservable } from 'mobx';
import moment from 'moment';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

export interface ITripStopEntityAttributes extends IModelAttributes {
	arrivalDateTime: Date;
	departureDateTime: Date;
	durationInMinutes?: number | null;

	ferryTripId: string;
	ferryTrip: Models.FerryTripEntity | Models.IFerryTripEntityAttributes;
	stopId: string;
	stop: Models.StopEntity | Models.IStopEntityAttributes;
}
@entity('TripStopEntity', 'Trip Stop')
export default class TripStopEntity extends Model implements ITripStopEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<TripStopEntity, Date>()
	public arrivalDateTime: Date;

	@observable
	@attribute<TripStopEntity, Date>()
	public departureDateTime: Date;

	/**
	 * How long the trip takes to reach this stop from the previous one, as entered on the trip form. Not stored:
	 * the server turns it into `arrivalDateTime`, and empty means the route's duration.
	 */
	@observable
	@attribute()
	public durationInMinutes?: number | null;

	@observable
	@attribute()
	public ferryTripId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public ferryTrip: Models.FerryTripEntity;

	@observable
	@attribute()
	public stopId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public stop: Models.StopEntity;

	constructor(attributes?: Partial<ITripStopEntityAttributes>) {
		super(attributes);

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<ITripStopEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.arrivalDateTime !== undefined) {
				if (attributes.arrivalDateTime === null) {
					this.arrivalDateTime = attributes.arrivalDateTime;
				} else {
					this.arrivalDateTime = moment(attributes.arrivalDateTime).toDate();
				}
			}
			if (attributes.departureDateTime !== undefined) {
				if (attributes.departureDateTime === null) {
					this.departureDateTime = attributes.departureDateTime;
				} else {
					this.departureDateTime = moment(attributes.departureDateTime).toDate();
				}
			}
			if (attributes.durationInMinutes !== undefined) {
				this.durationInMinutes = attributes.durationInMinutes;
			}
			if (attributes.ferryTripId !== undefined) {
				this.ferryTripId = attributes.ferryTripId;
			}
			if (attributes.ferryTrip !== undefined) {
				if (attributes.ferryTrip === null) {
					this.ferryTrip = attributes.ferryTrip;
				} else if (attributes.ferryTrip instanceof Models.FerryTripEntity) {
					this.ferryTrip = attributes.ferryTrip;
					this.ferryTripId = attributes.ferryTrip.id;
				} else {
					this.ferryTrip = new Models.FerryTripEntity(attributes.ferryTrip);
					this.ferryTripId = this.ferryTrip.id;
				}
			}
			if (attributes.stopId !== undefined) {
				this.stopId = attributes.stopId;
			}
			if (attributes.stop !== undefined) {
				if (attributes.stop === null) {
					this.stop = attributes.stop;
				} else if (attributes.stop instanceof Models.StopEntity) {
					this.stop = attributes.stop;
					this.stopId = attributes.stop.id;
				} else {
					this.stop = new Models.StopEntity(attributes.stop);
					this.stopId = this.stop.id;
				}
			}
		}
	}
}
